import React, { useState } from 'react';
import { Map, MapPin, Home, Eye, PenTool, Diamond, Building } from 'lucide-react';
import buildingsData from '../data/buildings.json';
import './MapPlan.css';

// Import your map images
import currentMapImage from '../assets/images/plans/current.png';
import futureMapImage from '../assets/images/plans/future.png';

const MapPlan: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Map },
    { id: 'buildings', label: 'Buildings', icon: MapPin },
  ];

  // Map icon names to actual icon components
  const iconMap = {
    Building,
    Diamond
  };

  const buildings = buildingsData.map(building => ({
    ...building,
    icon: iconMap[building.icon as keyof typeof iconMap] || Building
  }));

  const openFullscreen = (imageUrl: string) => {
    setFullscreenImage(imageUrl);
  };

  const closeFullscreen = () => {
    setFullscreenImage(null);
  };



  return (
    <section id="map" className="map-plan">
      <div className="container">
        <div className="map-header">
          <h2 className="section-title">Town Map & Development Plan</h2>
          <p className="section-description">
            Explore our comprehensive town layout, track development progress, and discover planned expansions.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="map-tabs">
          {tabs.map(tab => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                className={`map-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <IconComponent size={20} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content Area */}
        <div className="map-content-area">
          {activeTab === 'overview' && (
            <div className="map-overview-redesign">
              {/* Header Section */}
              <div className="overview-header glass-card">
                <div className="overview-title">
                  <h3>Town Overview</h3>
                  <p>Compare our current town with future building plans</p>
                </div>
                <div className="overview-stats">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <Home size={24} />
                    </div>
                    <div className="stat-content">
                      <span className="stat-number">5</span>
                      <span className="stat-label">Buildings</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dual Image Display */}
              <div className="dual-map-display">
                <div className="map-card current-map glass-card">
                  <div className="map-card-header">
                    <div className="map-card-title">
                      <Eye size={20} />
                      <h4>Current State</h4>
                    </div>
                    <div className="map-card-badge current">Live</div>
                  </div>
                  <div className="map-image-container">
                    <img 
                      src={currentMapImage}
                      alt="Current Town Layout"
                      className="map-image"
                    />
                    <div className="map-overlay">
                                           <div className="overlay-content">
                       <p>Our town as it is now</p>
                       <button className="view-fullscreen" onClick={() => openFullscreen(currentMapImage)}>
                         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                           <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                         </svg>
                         View Full Size
                       </button>
                     </div>
                    </div>
                  </div>
                </div>

                <div className="comparison-divider">
                  <div className="divider-line"></div>
                  <div className="divider-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14"/>
                      <path d="m12 5 7 7-7 7"/>
                    </svg>
                  </div>
                  <div className="divider-line"></div>
                </div>

                <div className="map-card future-map glass-card">
                  <div className="map-card-header">
                    <div className="map-card-title">
                      <PenTool size={20} />
                      <h4>Future Vision</h4>
                    </div>
                    <div className="map-card-badge future">Planned</div>
                  </div>
                  <div className="map-image-container">
                    <img 
                      src={futureMapImage}
                      alt="Future Development Plans"
                      className="map-image"
                    />
                    <div className="map-overlay">
                                           <div className="overlay-content">
                       <p>Our planned builds</p>
                       <button className="view-fullscreen" onClick={() => openFullscreen(futureMapImage)}>
                         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                           <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                         </svg>
                         View Full Size
                       </button>
                     </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Timeline */}
              <div className="progress-timeline glass-card">
                                 <h4>Building Timeline</h4>
                 <div className="timeline-content">
                   <div className="timeline-item completed">
                     <div className="timeline-dot"></div>
                     <div className="timeline-content-item">
                       <h5>Foundation Builds</h5>
                       <p>Basic houses, farms, and essential structures completed</p>
                       <span className="timeline-date">Completed</span>
                     </div>
                   </div>
                   <div className="timeline-item in-progress">
                     <div className="timeline-dot"></div>
                     <div className="timeline-content-item">
                       <h5>Town Expansion</h5>
                       <p>Adding more buildings, shops, and decorative structures</p>
                       <span className="timeline-date">In Progress</span>
                     </div>
                   </div>
                   <div className="timeline-item planned">
                     <div className="timeline-dot"></div>
                     <div className="timeline-content-item">
                       <h5>Mega Projects</h5>
                       <p>Castle, cathedral, and other large-scale builds</p>
                       <span className="timeline-date">Planned</span>
                     </div>
                   </div>
                 </div>
              </div>
            </div>
          )}

          {activeTab === 'buildings' && (
            <div className="buildings-overview">
              <div className="buildings-header glass-card">
                <h3>Factory & Building List</h3>
                <p>Complete list of all factories and buildings planned for our town</p>
              </div>
              
              <div className="buildings-grid">  
                {buildings.map(building => {
                  const IconComponent = building.icon;
                  
                  const getStatusLabel = (status: string) => {
                    switch (status) {
                      case 'not-started': return 'Pending';
                      case 'designing': return 'Planning';
                      case 'collecting': return 'Gathering';
                      case 'repairing': return 'Fixing';
                      case 'finished': return 'Done';
                      case 'completed': return 'Live';
                      case 'private': return 'Private';
                      default: return 'Unknown';
                    }
                  };

                  const getStatusColor = (status: string) => {
                    switch (status) {
                      case 'not-started': return '#6b7280';
                      case 'designing': return '#3b82f6';
                      case 'collecting': return '#f59e0b';
                      case 'repairing': return '#ef4444';
                      case 'finished': return '#8b5cf6';
                      case 'completed': return '#10b981';
                      case 'private': return '#f97316';
                      default: return '#6b7280';
                    }
                  };

                  return (
                    <div key={building.id} className="building-card-modern">
                      <div className="building-card-background" style={{ backgroundColor: `${building.color}15` }}>
                        <div className="building-card-content">
                          <div className="building-header-modern">
                            <div className="building-icon-modern" style={{ backgroundColor: building.color }}>
                              <IconComponent size={20} />
                            </div>
                            <div 
                              className="status-indicator" 
                              style={{ backgroundColor: getStatusColor(building.status) }}
                              title={getStatusLabel(building.status)}
                            />
                          </div>
                          
                          <div className="building-info">
                            <h3 className="building-name-modern">{building.name}</h3>
                            <p className="building-description-modern">{building.description}</p>
                          </div>
                          
                          <div className="building-footer">
                            <span 
                              className="status-text" 
                              style={{ color: getStatusColor(building.status) }}
                            >
                              {getStatusLabel(building.status)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="building-card-glow" style={{ backgroundColor: building.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Modal */}
      {fullscreenImage && (
        <div className="fullscreen-modal" onClick={closeFullscreen}>
          <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
            <button className="fullscreen-close" onClick={closeFullscreen}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
            <img src={fullscreenImage} alt="Full size map" className="fullscreen-image" />
          </div>
        </div>
      )}
    </section>
  );
};

export default MapPlan; 